import { v4 as uuidv4 } from 'uuid'
import { ActionsUnion, getCurrentWindowId } from 'utils'
import { clientActionEvent, clientActionSubscribe, clientActionUnsubscribe } from './ClientAction'
import {
  DealAllocateEvent,
  DealAmendEvent,
  DealAssignFundEvent,
  DealCancelAllocationsEvent,
  DealCancelAssignmentEvent,
  DealCancelEvent,
  DealClearingFailedManuallyAcceptedEvent,
  DealClearingFailedManuallyRejectedEvent,
  DealClearingFailedRetryEvent,
  DealExtendAllocationTimeEvent,
  DealSaveAllocationsEvent,
  DealSaveFundAssignmentEvent,
  DealServicingAcceptEvent,
  DealServicingCancelEvent,
  DealServicingRejectEvent,
  DealServicingResponderLockEvent,
  DealServicingResponderUnlockEvent,
  DealServicingRevertAttributesEvent,
  DealServicingSendEvent,
  DealServicingStartAdminCloseOpenEvent,
  DealServicingStartAdminCloseOpenEventBulk,
  DealServicingStartAmendEvent,
  DealServicingStartCloseOpenEvent,
  DealServicingStartCloseOpenEventBulk,
  DealServicingStartEventBulk,
  DealServicingStartRerateEventBulk,
  DealServicingStartReturnRecallEvent,
  DealServicingStartRollEvent,
  DealServicingStartRollInPlaceEventBulk,
  DealServicingStartSubstituteEvent,
  DealServicingStartTerminateEventBulk,
  DealServicingStartTraderAmendEvent,
  DealServicingStartTraderCancelEvent,
  DealServicingUpdateAttributesEvent,
  DealServicingWithdrawEvent,
  DealStartAllocationsEvent,
  DealStartFundAssignmentEvent,
  EntityTypeEnum,
  FilterOperation,
  SubscribeType,
  SubscriptionFilter
} from './generated'
import { EntityType } from './EntityType'
import { DealHistorySubscriptionIdentifier, DealSubscriptionIdentifier, EventSpecificArgs, SUBSCRIPTION_PAGE_SIZE } from './local'
import {
  CompositeEvent,
  DealEvent,
  DealServicingRespondEventBulk,
  DealServicingStartAdminRerateEvent,
  DealServicingStartAdminRerateEventBulk,
  DealServicingStartAdminSubstitutionEvent,
  DealServicingStartAdminTerminateEventBulk,
  DealServicingStartRerateEvent,
  DealServicingUpdateRegulatoryAttributesEvent,
  DealStatus
} from 'api'
import { getPageSize } from 'utils/pageSizeUtils'

function dealCancel(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealCancelEvent>('DealCancelEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

export type DealAmendArgs = Omit<DealAmendEvent, 'eventType' | 'correlationId'>

function dealAmend(args: DealAmendArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealAmendEvent>('DealAmendEvent', args, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

function dealStartAssignments(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealStartFundAssignmentEvent>('DealStartFundAssignmentEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealCancelAssignments(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealCancelAssignmentEvent>('DealCancelAssignmentEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

export type DealSaveAssignmentsArgs = Omit<DealSaveFundAssignmentEvent, 'eventType' | 'correlationId'>

function dealSaveAssignments(args: DealSaveAssignmentsArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealSaveFundAssignmentEvent>('DealSaveFundAssignmentEvent', args, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

function dealAssign(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealAssignFundEvent>('DealAssignFundEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealStartAllocations(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealStartAllocationsEvent>(
    'DealStartAllocationsEvent',
    { dealId: dealId, applyDefaultHaircut: false },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealCancelAllocations(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealCancelAllocationsEvent>('DealCancelAllocationsEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

export type DealSaveAllocationsArgs = Omit<DealSaveAllocationsEvent, 'eventType' | 'correlationId'>

function dealSaveAllocations(args: DealSaveAllocationsArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealSaveAllocationsEvent>('DealSaveAllocationsEvent', args, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

function dealAllocate(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealAllocateEvent>('DealAllocateEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealExtendAllocationTime(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealExtendAllocationTimeEvent>('DealExtendAllocationTimeEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealClearingFailureRetry(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealClearingFailedRetryEvent>('DealClearingFailedRetryEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealClearingFailureManuallyComplete(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealClearingFailedManuallyAcceptedEvent>('DealClearingFailedManuallyAcceptedEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealClearingFailureManuallyCancel(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealClearingFailedManuallyRejectedEvent>('DealClearingFailedManuallyRejectedEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealServicingStartAmend(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartAmendEvent>(
    'DealServicingStartAmendEvent',
    {
      dealId: dealId,
      externalServicingId: null
    },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingStartTraderAmend(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartTraderAmendEvent>(
    'DealServicingStartTraderAmendEvent',
    {
      dealId,
      externalServicingId: null
    },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingStartTraderCancel(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartTraderCancelEvent>(
    'DealServicingStartTraderCancelEvent',
    {
      dealId,
      externalServicingId: null
    },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

export type DealServicingStartSubstituteArgs = Omit<
  DealServicingStartSubstituteEvent,
  'eventType' | 'correlationId' | 'externalServicingId'
>

function dealServicingStartSubstitute(args: DealServicingStartSubstituteArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartSubstituteEvent>(
    'DealServicingStartSubstituteEvent',
    { ...args, externalServicingId: null },
    sourceWindowId,
    {
      entityIds: [args.dealId],
      entityType: EntityType.DEAL
    }
  )
}

export type DealServicingStartAdminSubstituteArgs = Omit<
  DealServicingStartAdminSubstitutionEvent,
  'eventType' | 'correlationId' | 'externalServicingId'
>

function dealServicingStartAdminSubstitute(args: DealServicingStartAdminSubstituteArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartAdminSubstitutionEvent>(
    'DealServicingStartAdminSubstitutionEvent',
    { ...args, externalServicingId: null },
    sourceWindowId,
    {
      entityIds: [args.dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingStartRoll(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartRollEvent>(
    'DealServicingStartRollEvent',
    { dealId, externalServicingId: null },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

export type DealServicingStartRollBulkArgs = EventSpecificArgs<CompositeEvent>

function dealServicingStartRollBulk(args: DealServicingStartRollBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('CompositeEvent', args, sourceWindowId)
}

export type DealServicingStartReturnRecallArgs = Omit<
  DealServicingStartReturnRecallEvent,
  'eventType' | 'correlationId' | 'externalServicingId'
>

function dealServicingStartReturnRecall(args: DealServicingStartReturnRecallArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartReturnRecallEvent>(
    'DealServicingStartReturnRecallEvent',
    { ...args, externalServicingId: null },
    sourceWindowId,
    {
      entityIds: [args.dealId],
      entityType: EntityType.DEAL
    }
  )
}

export type DealServicingStartRollInPlaceBulkArgs = Omit<
  DealServicingStartRollInPlaceEventBulk,
  'eventType' | 'correlationId' | 'externalServicingId'
>

function dealServicingStartRollInPlaceBulk(args: DealServicingStartRollInPlaceBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartRollInPlaceEventBulk>('DealServicingStartRollInPlaceEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealIds,
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartAdminTerminateEventBulkArgs = Omit<DealServicingStartAdminTerminateEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartAdminTerminateBulk(
  args: DealServicingStartAdminTerminateEventBulkArgs,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<DealServicingStartAdminTerminateEventBulk>(
    'DealServicingStartAdminTerminateEventBulk',
    { ...args },
    sourceWindowId,
    {
      entityIds: args.dealIds,
      entityType: EntityType.DEAL
    }
  )
}

export type DealServicingStartTerminateBulkArgs = Omit<DealServicingStartTerminateEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartTerminateBulk(args: DealServicingStartTerminateBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartTerminateEventBulk>('DealServicingStartTerminateEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealIds,
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartRerateArgs = Omit<DealServicingStartRerateEvent, 'eventType' | 'correlationId'>

function dealServicingStartRerate(args: DealServicingStartRerateArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartRerateEvent>('DealServicingStartRerateEvent', { ...args }, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartAdminRerateArgs = Omit<DealServicingStartAdminRerateEvent, 'eventType' | 'correlationId'>

function dealServicingStartAdminRerate(args: DealServicingStartAdminRerateArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartAdminRerateEvent>('DealServicingStartAdminRerateEvent', { ...args }, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartAdminRerateBulkArgs = Omit<DealServicingStartAdminRerateEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartAdminRerateBulk(args: DealServicingStartAdminRerateBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartAdminRerateEventBulk>('DealServicingStartAdminRerateEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealIds,
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartRerateBulkArgs = Omit<DealServicingStartRerateEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartRerateBulk(args: DealServicingStartRerateBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartRerateEventBulk>('DealServicingStartRerateEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealServicingStartRerateAttributesList.map(attr => attr.dealId),
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartCloseOpenArgs = Omit<DealServicingStartCloseOpenEvent, 'eventType' | 'correlationId'>

function dealServicingStartCloseOpen(args: DealServicingStartCloseOpenArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartCloseOpenEvent>('DealServicingStartCloseOpenEvent', { ...args }, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartAdminCloseOpenArgs = Omit<DealServicingStartAdminCloseOpenEvent, 'eventType' | 'correlationId'>

function dealServicingStartAdminCloseOpen(args: DealServicingStartAdminCloseOpenArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartAdminCloseOpenEvent>('DealServicingStartAdminCloseOpenEvent', { ...args }, sourceWindowId, {
    entityIds: [args.dealId],
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartAdminCloseOpenBulkArgs = Omit<DealServicingStartAdminCloseOpenEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartAdminCloseOpenBulk(
  args: DealServicingStartAdminCloseOpenBulkArgs,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<DealServicingStartAdminCloseOpenEventBulk>(
    'DealServicingStartAdminCloseOpenEventBulk',
    { ...args },
    sourceWindowId,
    {
      entityIds: args.dealIds,
      entityType: EntityType.DEAL
    }
  )
}

export type DealServicingStartCloseOpenBulkArgs = Omit<DealServicingStartCloseOpenEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartCloseOpenBulk(args: DealServicingStartCloseOpenBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartCloseOpenEventBulk>('DealServicingStartCloseOpenEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealServicingStartCloseOpenAttributesList.map(attr => attr.dealId),
    entityType: EntityType.DEAL
  })
}

export type DealServicingStartBulkArgs = Omit<DealServicingStartEventBulk, 'eventType' | 'correlationId'>

function dealServicingStartBulk(args: DealServicingStartBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingStartEventBulk>('DealServicingStartEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealServicingStartAttributesList.map(attr => attr.dealId),
    entityType: EntityType.DEAL
  })
}

export type DealServicingRespondBulkArgs = Omit<DealServicingRespondEventBulk, 'eventType' | 'correlationId'>

function dealServicingRespondBulk(args: DealServicingRespondBulkArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingRespondEventBulk>('DealServicingRespondEventBulk', { ...args }, sourceWindowId, {
    entityIds: args.dealServicingRespondAttributesList.map(attr => attr.dealId),
    entityType: EntityType.DEAL
  })
}

function dealServicingResponderLock(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingResponderLockEvent>('DealServicingResponderLockEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealServicingResponderUnlock(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingResponderUnlockEvent>('DealServicingResponderUnlockEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

export type DealServicingUpdateAttributesArgs = Omit<DealServicingUpdateAttributesEvent, 'eventType' | 'correlationId' | 'failOnValidation'>

function dealServicingUpdateAttributes(args: DealServicingUpdateAttributesArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingUpdateAttributesEvent>(
    'DealServicingUpdateAttributesEvent',
    { failOnValidation: false, ...args },
    sourceWindowId,
    {
      entityIds: [args.dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingRevertAttributes(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingRevertAttributesEvent>('DealServicingRevertAttributesEvent', { dealId }, sourceWindowId, {
    entityIds: [dealId],
    entityType: EntityType.DEAL
  })
}

function dealServicingUpdateRegulatoryAttributes(
  dealId: string,
  investmentDecisionMaker: string | null,
  executionDecisionMaker: string | null,
  revertFirst: boolean | null,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<DealServicingUpdateRegulatoryAttributesEvent>(
    'DealServicingUpdateRegulatoryAttributesEvent',
    { dealId, investmentDecisionMaker, executionDecisionMaker, revertFirst },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingSend(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingSendEvent>(
    'DealServicingSendEvent',
    { dealId, externalResponderServicingId: null },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingAccept(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingAcceptEvent>(
    'DealServicingAcceptEvent',
    {
      dealId,
      externalCounterId: null,
      externalTerminationServicingId: null,
      servicingType: null,
      replacedWithDealId: null
    },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

export function dealServicingAcceptBulk(dealIds: string[], sourceWindowId: string = getCurrentWindowId()) {
  const payload: EventSpecificArgs<CompositeEvent> = {
    childEvents: dealIds.map(dealId => ({
      dealId,
      eventType: 'DealServicingAcceptEvent',
      externalCounterId: null,
      externalTerminationServicingId: null,
      servicingType: null,
      replacedWithDealId: null,
      correlationId: uuidv4()
    }))
  }

  return clientActionEvent('CompositeEvent', payload, sourceWindowId, { entityIds: dealIds, entityType: EntityType.DEAL })
}

function dealServicingReject(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingRejectEvent>(
    'DealServicingRejectEvent',
    {
      dealId,
      servicingType: null,
      externalTerminationServicingId: null
    },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingWithdraw(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingWithdrawEvent>(
    'DealServicingWithdrawEvent',
    { dealId, externalTerminationServicingId: null, servicingType: null },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealServicingCancel(dealId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<DealServicingCancelEvent>(
    'DealServicingCancelEvent',
    { dealId, externalTerminationServicingId: null },
    sourceWindowId,
    {
      entityIds: [dealId],
      entityType: EntityType.DEAL
    }
  )
}

function dealRelatedSubscribe(originalDealIds: string[]) {
  return clientActionSubscribe({
    subscriptionIdentifier: DealSubscriptionIdentifier.DEAL_HISTORY,
    entityTypeEnum: EntityTypeEnum.DEAL_HISTORY,
    preDefinedFilter: null,
    filters: [
      {
        fieldName: 'originalDealId',
        filterOperation: FilterOperation.CONTAINS,
        calculatedValueType: null,
        value: originalDealIds.join(','),
        orFilter: null,
        andFilter: null
      },
      {
        calculatedValueType: null,
        fieldName: 'status',
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        value: [DealStatus.DONE, DealStatus.CANCELLED].join(','),
        andFilter: {
          fieldName: 'lastEvent',
          filterOperation: FilterOperation.NOT_CONTAINS,
          value: [
            DealEvent.SERVICING_REJECT,
            DealEvent.SERVICING_WITHDRAW,
            DealEvent.SERVICING_CANCEL,
            DealEvent.RECALCULATE,
            DealEvent.UPDATE_AVAILABLE_EVENTS
          ].join(','),
          orFilter: null,
          andFilter: null,
          calculatedValueType: null
        }
      }
    ],
    postTransformFilters: [],
    subscribeType: SubscribeType.FILTERED,
    limitResults: true,
    pageNumber: null,
    pageSize: null,
    summaryRequest: null
  })
}

function dealRelatedUnsubscribeAll() {
  return clientActionUnsubscribe({
    subscriptionIdentifier: DealSubscriptionIdentifier.DEAL_HISTORY,
    entityTypeEnum: EntityTypeEnum.DEAL_HISTORY
  })
}

function dealInterestPeriodSubscribe(originalDealIds: string[]) {
  return clientActionSubscribe({
    subscriptionIdentifier: DealSubscriptionIdentifier.DEAL_INTEREST_PERIOD,
    entityTypeEnum: EntityTypeEnum.DEAL_INTEREST_PERIOD,
    preDefinedFilter: null,
    filters: [
      {
        fieldName: 'originalDealId',
        filterOperation: FilterOperation.CONTAINS,
        value: originalDealIds.join(','),
        calculatedValueType: null,
        orFilter: null,
        andFilter: null
      }
    ],
    postTransformFilters: [],
    subscribeType: SubscribeType.FILTERED,
    limitResults: true,
    pageNumber: null,
    pageSize: null,
    summaryRequest: null
  })
}

function dealInterestPeriodUnsubscribeAll() {
  return clientActionUnsubscribe({
    subscriptionIdentifier: DealSubscriptionIdentifier.DEAL_INTEREST_PERIOD,
    entityTypeEnum: EntityTypeEnum.DEAL_INTEREST_PERIOD
  })
}

function dealPageSubscribe(subscriptionIdentifier: DealSubscriptionIdentifier, filters: SubscriptionFilter[], pageNumber: number = 0) {
  return clientActionSubscribe({
    entityTypeEnum: EntityTypeEnum.DEAL,
    preDefinedFilter: null,
    filters,
    postTransformFilters: [],
    subscribeType: SubscribeType.PAGE,
    subscriptionIdentifier,
    limitResults: false,
    pageSize: getPageSize(subscriptionIdentifier),
    pageNumber,
    summaryRequest: null
  })
}

function dealHistoryPageSubscribe(
  subscriptionIdentifier: DealHistorySubscriptionIdentifier,
  filters: SubscriptionFilter[],
  pageNumber: number = 0
) {
  return clientActionSubscribe({
    entityTypeEnum: EntityTypeEnum.DEAL_HISTORY,
    preDefinedFilter: null,
    filters,
    postTransformFilters: [],
    subscribeType: SubscribeType.PAGE,
    subscriptionIdentifier,
    limitResults: false,
    pageSize: SUBSCRIPTION_PAGE_SIZE,
    pageNumber,
    summaryRequest: null
  })
}

export function dealServicingTradeBulkCancel(dealIds: string[], eventType: string) {
  const sourceWindowId: string = getCurrentWindowId()
  const mapDealServicingTradeBulkCancelEvent = (dealId: string) => {
    return {
      dealId,
      eventType: eventType,
      correlationId: uuidv4(),
      externalServicingId: null
    }
  }

  const childEvents = dealIds.map(mapDealServicingTradeBulkCancelEvent)
  return clientActionEvent('CompositeEvent', { childEvents }, sourceWindowId, { entityIds: dealIds, entityType: EntityType.DEAL })
}

export const DealActions = {
  cancel: dealCancel,
  amend: dealAmend,
  startAssignments: dealStartAssignments,
  cancelAssignments: dealCancelAssignments,
  saveAssignments: dealSaveAssignments,
  assign: dealAssign,
  startAllocations: dealStartAllocations,
  cancelAllocations: dealCancelAllocations,
  saveAllocations: dealSaveAllocations,
  allocate: dealAllocate,
  extendAllocationTime: dealExtendAllocationTime,
  clearingFailureRetry: dealClearingFailureRetry,
  clearingFailureManuallyComplete: dealClearingFailureManuallyComplete,
  clearingFailureManuallyCancel: dealClearingFailureManuallyCancel,
  servicingCancel: dealServicingCancel,
  servicingStartAmend: dealServicingStartAmend,
  servicingStartTraderAmend: dealServicingStartTraderAmend,
  servicingStartTraderCancel: dealServicingStartTraderCancel,
  servicingStartSubstitute: dealServicingStartSubstitute,
  servicingStartAdminSubstitute: dealServicingStartAdminSubstitute,
  servicingStartReturnRecall: dealServicingStartReturnRecall,
  servicingStartRoll: dealServicingStartRoll,
  servicingStartRollBulk: dealServicingStartRollBulk,
  servicingStartRollInPlaceBulk: dealServicingStartRollInPlaceBulk,
  servicingStartTerminateBulk: dealServicingStartTerminateBulk,
  servicingStartAdminTerminateBulk: dealServicingStartAdminTerminateBulk,
  servicingStartRerate: dealServicingStartRerate,
  servicingStartRerateBulk: dealServicingStartRerateBulk,
  servicingStartAdminRerate: dealServicingStartAdminRerate,
  servicingStartAdminRerateBulk: dealServicingStartAdminRerateBulk,
  servicingStartCloseOpen: dealServicingStartCloseOpen,
  servicingStartCloseOpenBulk: dealServicingStartCloseOpenBulk,
  servicingStartAdminCloseOpen: dealServicingStartAdminCloseOpen,
  servicingStartAdminCloseOpenBulk: dealServicingStartAdminCloseOpenBulk,
  servicingStartBulk: dealServicingStartBulk,
  servicingRespondBulk: dealServicingRespondBulk,
  servicingResponderLock: dealServicingResponderLock,
  servicingResponderUnlock: dealServicingResponderUnlock,
  servicingUpdateAttributes: dealServicingUpdateAttributes,
  servicingRevertAttributes: dealServicingRevertAttributes,
  servicingUpdateRegulatoryAttributes: dealServicingUpdateRegulatoryAttributes,
  servicingSend: dealServicingSend,
  servicingAccept: dealServicingAccept,
  servicingAcceptBulk: dealServicingAcceptBulk,
  servicingReject: dealServicingReject,
  servicingWithdraw: dealServicingWithdraw,
  relatedSubscribe: dealRelatedSubscribe,
  relatedUnsubscribeAll: dealRelatedUnsubscribeAll,
  interestPeriodSubscribe: dealInterestPeriodSubscribe,
  interestPeriodUnsubscribeAll: dealInterestPeriodUnsubscribeAll,
  pageSubscribe: dealPageSubscribe,
  pageHistorySubscribe: dealHistoryPageSubscribe,
  servicingBulkCancel: dealServicingTradeBulkCancel
}

export type DealActions = ActionsUnion<typeof DealActions>
